import { motion } from 'framer-motion';
import {
  createChart,
  CrosshairMode,
  LineStyle,
  type IChartApi,
  type ISeriesApi,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import { useEffect, useRef, type ReactNode } from 'react';
import { useResolvedTheme } from '../../lib/useResolvedTheme';

export type ScrubPoint = { time: string; value: number };

export type ScrubMarker = {
  time: string;
  position: 'aboveBar' | 'belowBar' | 'inBar';
  color: string;
  shape: 'arrowUp' | 'arrowDown' | 'circle' | 'square';
  text?: string;
};

type Props = {
  data: ScrubPoint[];
  height?: number;
  color?: string;
  areaFill?: boolean;
  /** Optional secondary horizontal price line (e.g. pace target). */
  priceLine?: { value: number; color?: string; title?: string } | null;
  /** Optional event markers (income/bill days). */
  markers?: ScrubMarker[];
  onScrub?: (point: ScrubPoint | null) => void;
  emptyState?: ReactNode;
};

function isoToTime(iso: string): UTCTimestamp {
  // Lightweight-charts wants UTC seconds. Parse as midnight UTC of that day.
  return Math.floor(Date.UTC(
    Number(iso.slice(0, 4)),
    Number(iso.slice(5, 7)) - 1,
    Number(iso.slice(8, 10)),
  ) / 1000) as UTCTimestamp;
}

function timeToIso(t: Time): string {
  if (typeof t === 'number') {
    const d = new Date(t * 1000);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
  }
  if (typeof t === 'string') return t;
  return `${t.year}-${String(t.month).padStart(2, '0')}-${String(t.day).padStart(2, '0')}`;
}

function withAlpha(hex: string, alpha: number): string {
  // Accept #RRGGBB or rgb(...) — emit rgba(r,g,b,a).
  if (hex.startsWith('#') && hex.length === 7) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return hex;
}

/**
 * Robinhood-style finger-scrub chart powered by lightweight-charts.
 * Single series (line or area) with a crosshair that follows touch
 * input. Parent owns the hero label/value display via onScrub.
 */
export function ScrubChart({
  data,
  height = 200,
  color = '#0EA66B',
  areaFill = true,
  priceLine = null,
  markers,
  onScrub,
  emptyState,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Area'> | ISeriesApi<'Line'> | null>(null);
  const onScrubRef = useRef(onScrub);
  onScrubRef.current = onScrub;
  const theme = useResolvedTheme();

  const hasData = data.length >= 2;

  useEffect(() => {
    if (!hasData) return;
    const el = containerRef.current;
    if (!el) return;

    const isDark = theme === 'dark';
    const textColor = isDark ? '#71717A' : '#9CA3AF';
    const surfaceBg = isDark ? '#18181B' : '#FFFFFF';
    const crosshairColor = '#9CA3AF66';

    const chart = createChart(el, {
      width: el.clientWidth,
      height,
      layout: {
        background: { color: 'transparent' },
        textColor,
        fontFamily: 'inherit',
        attributionLogo: false,
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { visible: false },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: crosshairColor,
          width: 1,
          style: LineStyle.Solid,
          labelVisible: false,
        },
        horzLine: { visible: false, labelVisible: false },
      },
      rightPriceScale: { borderVisible: false, visible: false },
      leftPriceScale: { borderVisible: false, visible: false },
      timeScale: { borderVisible: false, visible: false, fixLeftEdge: true, fixRightEdge: true },
      handleScroll: false,
      handleScale: false,
    });
    chartRef.current = chart;

    const seriesOpts = {
      lineColor: color,
      lineWidth: 2 as const,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: true,
      crosshairMarkerRadius: 5,
      crosshairMarkerBorderColor: color,
      crosshairMarkerBackgroundColor: surfaceBg,
    };
    const series = areaFill
      ? chart.addAreaSeries({
          ...seriesOpts,
          topColor: withAlpha(color, 0.12),
          bottomColor: withAlpha(color, 0),
        })
      : chart.addLineSeries(seriesOpts);
    seriesRef.current = series;

    series.setData(
      data.map((d) => ({ time: isoToTime(d.time), value: d.value })),
    );

    if (priceLine) {
      series.createPriceLine({
        price: priceLine.value,
        color: priceLine.color ?? '#9CA3AF',
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: false,
        title: priceLine.title ?? '',
      });
    }

    if (markers && markers.length > 0) {
      series.setMarkers(
        markers.map((m) => ({
          time: isoToTime(m.time),
          position: m.position,
          color: m.color,
          shape: m.shape,
          text: m.text,
        })),
      );
    }

    chart.timeScale().fitContent();

    // Defensive: strip the TradingView attribution anchor if the option
    // was ignored (older lightweight-charts builds don't honor
    // attributionLogo: false). Run on a microtask so the canvas + DOM
    // chrome have already mounted.
    queueMicrotask(() => {
      el.querySelectorAll('a[href*="tradingview.com"]').forEach((node) => node.remove());
    });

    const lookup = new Map(data.map((d) => [d.time, d.value]));
    chart.subscribeCrosshairMove((param) => {
      const cb = onScrubRef.current;
      if (!cb) return;
      if (!param.point || !param.time) {
        cb(null);
        return;
      }
      const iso = timeToIso(param.time);
      const value = lookup.get(iso);
      if (value === undefined) {
        cb(null);
        return;
      }
      cb({ time: iso, value });
    });

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && chartRef.current) chartRef.current.applyOptions({ width: w });
    });
    ro.observe(el);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
    // Recreate on data identity, color, theme, or fill mode change.
  }, [data, color, areaFill, height, theme, priceLine, markers, hasData]);

  if (!hasData) {
    return <>{emptyState ?? null}</>;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      ref={containerRef}
      style={{ width: '100%', height }}
    />
  );
}
